import {
  PersistedExternalDocument,
  UploadExternalDocumentPayload,
} from '@ardoq/api-types';
import {
  CSRF_HEADER_NAME,
  crudApi,
  fetchJson,
  getCsrfToken,
  Method,
} from './api';
import { ApiResponse } from './types';

/**
 * Uploads a file to create an external document.
 * The file is stored and associated with the specified provider and external ID.
 *
 * @param payload - Upload configuration
 * @param payload.file - The file to upload
 * @param payload.provider - The external document provider (e.g., 'visual-importer', 'lucidchart')
 * @param payload.displayText - Display name for the document
 * @param payload.externalId - Optional external identifier for the document
 * @returns Promise resolving to the persisted external document with URL
 */
const uploadFile = async ({
  file,
  provider,
  displayText,
  externalId,
}: UploadExternalDocumentPayload): ApiResponse<PersistedExternalDocument> => {
  const params = new URLSearchParams({
    provider,
    displayText,
  });

  if (externalId) {
    params.set('externalId', externalId);
  }

  const url = `/api/external-document/content/upload?${params.toString()}`;

  const formData = new FormData();
  formData.append('file', file);

  const token = getCsrfToken();
  const request = new Request(url, {
    method: Method.POST,
    headers: token ? { [CSRF_HEADER_NAME]: token } : undefined,
    body: formData,
  });

  return fetchJson<PersistedExternalDocument>(request);
};

export const externalDocumentApi = {
  ...crudApi<PersistedExternalDocument>('external-document'),
  uploadFile,
};
